import React from 'react';

/**
 * RiskReversal Component
 *
 * Reduces perceived risk for signup by explicitly listing all trial benefits.
 * Displays "Your Free Trial Includes" section with specific guarantees.
 *
 * This component is designed to build trust with prospective users by being
 * transparent about what they get in their free trial, reducing anxiety
 * about commitment.
 */
export const RiskReversal: React.FC = () => {
  const trialBenefits = [
    {
      text: 'Full access to all 10,000+ questions (not limited)',
      icon: '🔓',
      color: 'green'
    },
    {
      text: 'Complete analytics dashboard (not demo version)',
      icon: '📊',
      color: 'blue'
    },
    {
      text: 'No credit card required for 30 days',
      icon: '💳',
      color: 'yellow'
    },
  ];

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string }> = {
      green: { bg: 'bg-green-100', text: 'text-green-600' },
      blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
      yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
      indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' }
    };
    return colorMap[color] || colorMap.green;
  };

  return (
    <div className="py-16 bg-gradient-to-b from-warm-gray to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-xl p-8 sm:p-12 border border-green-100">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-block mb-4">
              <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md">
                Zero Risk - 100% Value
              </span>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-3">
              Your Free Trial Includes
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the full platform with absolutely no limitations or commitments
            </p>
          </div>

          {/* Benefits List */}
          <div className="space-y-5">
            {trialBenefits.map((benefit, index) => {
              const colors = getColorClasses(benefit.color);
              return (
                <div
                  key={index}
                  className="flex items-start bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow animate-scale-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex-shrink-0">
                    <div className={`flex items-center justify-center h-12 w-12 rounded-full ${colors.bg} ${colors.text} text-2xl`}>
                      {benefit.icon}
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <p className="text-base font-semibold text-gray-900 leading-relaxed">
                      {benefit.text}
                    </p>
                  </div>
                  <div className="flex-shrink-0 ml-2">
                    <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom guarantee */}
          <div className="mt-8 pt-6 border-t border-green-200">
            <p className="text-center text-gray-700 text-base font-medium">
              Cancel in 1-click anytime. No phone calls, no hassle, no questions asked.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskReversal;
